import { RequestHandler, IRequestHandler } from '../lib/RequestHandler'
import { store } from '../store'
import { expenses } from '../store/expenses/slice'
import { CreateExpenseResponse, GetAllExpensesResponse, UpdateExpenseResponse } from '../ts/interfaces/request'
import { ExpenseStatus, DownloadExpensesBulkType, ExpensesFilterBy, DownloadExpenseType, Expense, ReviewStatus } from '../ts/interfaces/expenses'
import FileDownload from 'js-file-download'
import moment from 'moment'
import { localDateToUtc, getBrowserTimezone } from '../utils/date'
import { SortType } from '../ts/interfaces/hooks'
import { ReceiptStatus } from '../ts/interfaces/invoices'
import { AxiosError } from 'axios'
import { router, routerObj } from '../components/routing/Router'
import i18n from '../i18n'
export interface IExpensesFilter {
  start_date: Date
  end_date: Date
  user_id?: number | string | null
  business_id?: number | null
  tag_ids?: number[] | null
  filter_by?: ExpensesFilterBy
  page?: number
  page_size?: number
  sort_by?: string
  sort_type?: SortType
  status?: ReceiptStatus | null
  review_status?: ReviewStatus | null
  reviewer_id?: number | null
  include_archived?: boolean | null
  archived_only?: boolean | null
}
import fileDownload from 'js-file-download'
import { IExpenseForm } from '../components/ui/ExpenseForm'

export interface ExpenseSearchResult {
  id: number
  public_id: string
  name: string
  amount: number
  date: string
  merchant: string | null
  user_name: string | null
  user_email: string
  category: string
  is_linked_to_transaction: boolean
}

export interface ExpenseSearchResponse {
  data: ExpenseSearchResult[]
  total: number
}

const getSearchUrlParamsFilter = ({ end_date, start_date, business_id, tag_ids, user_id, filter_by, page, page_size, sort_by, sort_type, status, review_status, include_archived, archived_only }: IExpensesFilter): URLSearchParams => {
  const iso_start_date = moment(start_date).format('YYYY-MM-DD')
  const iso_end_date = moment(end_date).format('YYYY-MM-DD')
  const urlParams = new URLSearchParams({
    start_date: iso_start_date,
    end_date: iso_end_date,
    timezone: getBrowserTimezone()
  })

  if (user_id && user_id !== 0) urlParams.set('user_id', user_id.toString())
  if (page !== undefined && page !== null) urlParams.set('page', page.toString())
  if (page_size !== undefined && page_size !== null) urlParams.set('page_size', page_size.toString())
  if (business_id && business_id !== 0) urlParams.set('business_id', business_id.toString())
  if (tag_ids && tag_ids.length > 0) {
    tag_ids.forEach((id) => urlParams.append('tag_ids', id.toString()))
  }
  if (filter_by) urlParams.set('filter_by', filter_by)
  if (sort_by) urlParams.set('sort_by', sort_by)
  if (sort_type) urlParams.set('sort_type', sort_type)
  if (status && (status as string) !== 'ALL') urlParams.set('status', status)
  if (review_status && (review_status as string) !== 'ALL') urlParams.set('review_status', review_status)
  if (include_archived !== undefined && include_archived !== null) urlParams.set('include_archived', include_archived.toString())
  if (archived_only) urlParams.set('archived_only', 'true')

  return urlParams
}
export const ExpensesService = (props?: IRequestHandler) => ({
  requestHandler: RequestHandler(props || {}),

  async getExpenseDetails(id: string | number): Promise<ExpenseStatus | void> {
    try {
      const response = await this.requestHandler.get<{ data: ExpenseStatus }>(`expenses/${id}`)
      return response.data.data
    } catch (error) {
      const errorStatus = ((error as AxiosError).response?.status ? (error as AxiosError).response?.status : (error as AxiosError).status) as number

      if ([401, 403].includes(errorStatus)) {
        router.navigate(routerObj.expensesDashboard.path)
        return
      }
      throw error
    }
  },

  async getAllExpenses(params: IExpensesFilter): Promise<GetAllExpensesResponse> {
    try {
      const urlSearchParams = getSearchUrlParamsFilter(params)

      const response = await this.requestHandler.get<GetAllExpensesResponse>(`expenses?${urlSearchParams}`)

      if (response.data.data) {
        store.dispatch(
          expenses.actions.setExpensesList({
            expenses: response.data.data,
            total: response.data.total
          })
        )
        return response.data
      }
      return {
        data: [],
        total: 0
      }
    } catch (error) {
      console.error(error)
      store.dispatch(
        expenses.actions.setExpensesList({
          expenses: [],
          total: 0
        })
      )
      return {
        data: [],
        total: 0
      }
    }
  },

  async downloadExpensesData(download_type: DownloadExpensesBulkType, filter: IExpensesFilter, fields: string[], isReviewMode?: boolean): Promise<void> {
    try {
      const urlSearchParams = getSearchUrlParamsFilter(filter)
      urlSearchParams.set('download_type', download_type)
      urlSearchParams.set('is_review_mode', isReviewMode ? 'True' : 'False')

      if (fields && fields.length > 0) {
        fields.forEach((field) => {
          urlSearchParams.append('fields', field)
        })
      }

      const response = await this.requestHandler.post(
        `expenses/download/bulk?${urlSearchParams}`,
        {},
        {
          timeout: 900000,
          responseType: 'blob'
        }
      )

      const types: Record<DownloadExpensesBulkType, string> = {
        [DownloadExpensesBulkType.CSV]: 'csv',
        [DownloadExpensesBulkType.ZIP_IMAGES_PDF]: 'zip',
        [DownloadExpensesBulkType.ZIP_PDF_XML]: 'zip',
        [DownloadExpensesBulkType.BUDGET]: 'xlsx'
      }

      const prefix = download_type === DownloadExpensesBulkType.BUDGET ? i18n.t('expenses.expenses.budgetsFilePrefix') : i18n.t('expenses.expenses.expensesFilePrefix')
      FileDownload(response.data, `${prefix}_${moment(filter.start_date).format('DD-MM-YYYY')}_${moment(filter.end_date).format('DD-MM-YYYY')}.${types[download_type]}`)
    } catch (error) {
      throw error
    }
  },

  async startAsyncDownload(download_type: DownloadExpensesBulkType, filter: IExpensesFilter, fields: string[], isReviewMode?: boolean, fieldLabels?: Record<string, string>): Promise<{ job_id: string }> {
    const urlSearchParams = getSearchUrlParamsFilter(filter)
    urlSearchParams.set('download_type', download_type)
    urlSearchParams.set('is_review_mode', isReviewMode ? 'True' : 'False')

    if (fields && fields.length > 0) {
      fields.forEach((field) => urlSearchParams.append('fields', field))
    }

    if (fieldLabels && Object.keys(fieldLabels).length > 0) {
      urlSearchParams.set('field_labels', JSON.stringify(fieldLabels))
    }

    const response = await this.requestHandler.post<{ job_id: string }>(`expenses/download/bulk/async?${urlSearchParams}`, {})
    return response.data
  },

  async getDownloadStatus(jobId: string): Promise<{
    job_id: string
    status: 'pending' | 'processing' | 'completed' | 'failed'
    progress: number
    download_url: string | null
    file_name: string | null
    error: string | null
    total_expenses: number | null
    processed_expenses: number | null
    current_phase: string | null
  }> {
    const response = await this.requestHandler.get<{
      job_id: string
      status: 'pending' | 'processing' | 'completed' | 'failed'
      progress: number
      download_url: string | null
      file_name: string | null
      error: string | null
      total_expenses: number | null
      processed_expenses: number | null
      current_phase: string | null
    }>(`expenses/download/status/${jobId}`)
    return response.data
  },

  async downloadExpense(downloadType: DownloadExpenseType, expenseId: number, fileName: string): Promise<void> {
    try {
      const urlSearchParams = new URLSearchParams({
        type: downloadType
      })

      const response = await this.requestHandler.get<Blob>(`expenses/download/${expenseId}?${urlSearchParams}`, {
        responseType: 'blob',
        headers: {
          'Access-Control-Allow-Origin': '*'
        }
      })

      fileDownload(response.data, fileName)
    } catch (error) {
      throw error
    }
  },
  async createExpense(body: IExpenseForm): Promise<CreateExpenseResponse['data']> {
    try {
      const response = await this.requestHandler.post<CreateExpenseResponse>(`expenses`, {
        ...body,
        date: localDateToUtc(body.date)
      })
      return response.data.data
    } catch (error) {
      throw error
    }
  },
  async updateExpense(expenseId: Expense['id'], body: Partial<IExpenseForm>): Promise<UpdateExpenseResponse['data']> {
    try {
      const { image, pdf, invoice_pdf, invoice_xml, ...rest } = body
      const cleanBody = Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null))
      // Only send file fields when at least one has a new value;
      // include the null counterpart so the backend clears it.
      if (image || pdf) Object.assign(cleanBody, { image: image ?? null, pdf: pdf ?? null })
      const response = await this.requestHandler.patch<UpdateExpenseResponse>(`expenses/${expenseId}`, {
        ...cleanBody,
        ...(body.date && { date: localDateToUtc(body.date) })
      })
      return response.data.data
    } catch (error) {
      throw error
    }
  },

  async denyExpense(expenseId: number, notes: string): Promise<void> {
    try {
      await this.requestHandler.post(`expenses/${expenseId}/deny`, { notes })
    } catch (error) {
      throw error
    }
  },

  async approveExpense(expenseId: number, notes?: string): Promise<void> {
    try {
      await this.requestHandler.post(`expenses/${expenseId}/approve`, { notes })
    } catch (error) {
      throw error
    }
  },

  async bulkApproveExpenses(
    expenseIds: number[],
    notes?: string
  ): Promise<{
    successful: number[]
    failed: Array<{ expense_id: number; reason: string }>
    total_processed: number
    total_successful: number
    total_failed: number
  }> {
    try {
      const response = await this.requestHandler.post<{
        successful: number[]
        failed: Array<{ expense_id: number; reason: string }>
        total_processed: number
        total_successful: number
        total_failed: number
      }>('expenses/bulk/approve', {
        expense_ids: expenseIds,
        notes
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  async bulkDenyExpenses(
    expenseIds: number[],
    notes?: string
  ): Promise<{
    successful: number[]
    failed: Array<{ expense_id: number; reason: string }>
    total_processed: number
    total_successful: number
    total_failed: number
  }> {
    try {
      const response = await this.requestHandler.post<{
        successful: number[]
        failed: Array<{ expense_id: number; reason: string }>
        total_processed: number
        total_successful: number
        total_failed: number
      }>('expenses/bulk/deny', {
        expense_ids: expenseIds,
        notes
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  async getPendingApprovals(params: IExpensesFilter): Promise<GetAllExpensesResponse> {
    try {
      const urlSearchParams = getSearchUrlParamsFilter(params)

      const response = await this.requestHandler.get<GetAllExpensesResponse>(`expenses/pending-approvals?${urlSearchParams}`)

      if (response.data.data) {
        store.dispatch(
          expenses.actions.setExpensesList({
            expenses: response.data.data,
            total: response.data.total
          })
        )
        return response.data
      }
      return {
        data: [],
        total: 0
      }
    } catch (error) {
      console.error(error)
      store.dispatch(
        expenses.actions.setExpensesList({
          expenses: [],
          total: 0
        })
      )
      return {
        data: [],
        total: 0
      }
    }
  },

  async getReviewerExpenses(params: IExpensesFilter): Promise<GetAllExpensesResponse> {
    try {
      const urlSearchParams = getSearchUrlParamsFilter(params)

      const response = await this.requestHandler.get<GetAllExpensesResponse>(`expenses/reviewer-expenses?${urlSearchParams}`)

      if (response.data.data) {
        store.dispatch(
          expenses.actions.setExpensesList({
            expenses: response.data.data,
            total: response.data.total
          })
        )
        return response.data
      }
      return {
        data: [],
        total: 0
      }
    } catch (error) {
      console.error(error)
      store.dispatch(
        expenses.actions.setExpensesList({
          expenses: [],
          total: 0
        })
      )
      return {
        data: [],
        total: 0
      }
    }
  },

  async archiveExpenses(expenseIds: number[]): Promise<{
    successful: number[]
    failed: Array<{ expense_id: number; reason: string }>
    total_processed: number
    total_successful: number
    total_failed: number
  }> {
    try {
      const response = await this.requestHandler.post<{
        successful: number[]
        failed: Array<{ expense_id: number; reason: string }>
        total_processed: number
        total_successful: number
        total_failed: number
      }>('expenses/bulk/archive', {
        expense_ids: expenseIds
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  async unarchiveExpenses(expenseIds: number[]): Promise<{
    successful: number[]
    failed: Array<{ expense_id: number; reason: string }>
    total_processed: number
    total_successful: number
    total_failed: number
  }> {
    try {
      const response = await this.requestHandler.post<{
        successful: number[]
        failed: Array<{ expense_id: number; reason: string }>
        total_processed: number
        total_successful: number
        total_failed: number
      }>('expenses/bulk/unarchive', {
        expense_ids: expenseIds
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  async searchExpenses(params: { query?: string; min_amount?: number; max_amount?: number; start_date?: Date; end_date?: Date; merchant?: string; user_id?: number; skip?: number; limit?: number }): Promise<ExpenseSearchResponse> {
    const urlParams = new URLSearchParams()

    if (params.query) urlParams.set('query', params.query)
    if (params.min_amount !== undefined) urlParams.set('min_amount', params.min_amount.toString())
    if (params.max_amount !== undefined) urlParams.set('max_amount', params.max_amount.toString())
    if (params.start_date) urlParams.set('start_date', params.start_date.toISOString())
    if (params.end_date) urlParams.set('end_date', params.end_date.toISOString())
    if (params.merchant) urlParams.set('merchant', params.merchant)
    if (params.user_id !== undefined) urlParams.set('user_id', params.user_id.toString())
    if (params.skip !== undefined) urlParams.set('skip', params.skip.toString())
    if (params.limit !== undefined) urlParams.set('limit', params.limit.toString())

    const response = await this.requestHandler.get<ExpenseSearchResponse>(`expenses/search?${urlParams}`)
    return response.data
  }
})
